import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import {
  BOOKING_STATUS_LABELS,
  OWNER_TIMEZONE,
  PURCHASE_KINDS,
  PURCHASE_KIND_LABELS,
  clientBalances,
  clientName,
  tagColorMap,
  formatPrice,
  zlotyToGrosz,
  type BookingStatus,
  type Client,
  type ClientInput,
  type PurchaseInput,
  type PurchaseKind,
} from '@diana/shared'
import { api } from '../../lib/api.js'
import { isoDateIn, timeIn } from '../../lib/datetime.js'
import { Column, Main } from '../../classroom/Shell.js'
import { Avatar, Button, ErrorLine, FieldLabel, Skeleton, Stat, StatGrid, Status, type Tone } from '../../classroom/ui.js'
import { ClientForm } from './ClientForm.js'
import { PlanSection } from './PlanSection.js'

const statusTone: Record<BookingStatus, Tone> = {
  confirmed: 'ok',
  pending: 'wait',
  completed: 'neutral',
  cancelled: 'bad',
  no_show: 'bad',
}

/**
 * One student (Studio-Client.png): who they are, the three numbers Diana is
 * asked about („ile lekcji mi zostało?”), their courses with „Aktywuj kurs”,
 * what they bought, every lesson, the notes and the platform account.
 */
export function ClientDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [buying, setBuying] = useState(false)

  const { data, isLoading } = useQuery({ queryKey: ['studio', 'client', id], queryFn: () => api.studio.client(id), enabled: id.length > 0 })
  const tagsQuery = useQuery({ queryKey: ['studio', 'tags'], queryFn: api.studio.tags })
  const tagColors = useMemo(() => tagColorMap(tagsQuery.data ?? []), [tagsQuery.data])

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['studio', 'client', id] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'clients'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'accounting'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'dashboard'] })
  }

  const update = useMutation({
    mutationFn: (input: ClientInput) => api.studio.updateClient(id, input),
    onSuccess: () => {
      refresh()
      setEditing(false)
    },
  })
  const remove = useMutation({
    mutationFn: () => api.studio.deleteClient(id),
    onSuccess: () => {
      refresh()
      navigate('/studio/klienci', { replace: true })
    },
    onError: (e: unknown) =>
      window.alert(
        (e as { message?: string }).message === 'has_history'
          ? 'Ten uczeń ma lekcje lub zakupy w rozliczeniach — usunięcie zmieniłoby przeszłe miesiące. Odznacz „Aktywny”, a zniknie z list, zostając w rozliczeniach.'
          : 'Nie udało się usunąć ucznia.',
      ),
  })
  const addPurchase = useMutation({
    mutationFn: (input: PurchaseInput) => api.studio.addPurchase(id, input),
    onSuccess: () => {
      refresh()
      setBuying(false)
    },
  })
  const deletePurchase = useMutation({ mutationFn: api.studio.deletePurchase, onSuccess: refresh })

  const today = isoDateIn(new Date(), OWNER_TIMEZONE)
  const balance = useMemo(() => {
    if (!data) return null
    const lessons = data.lessons
      .filter((l) => l.status !== 'cancelled')
      .map((l) => ({ clientId: l.clientId, day: l.day, status: l.status, priceGrosz: l.priceGrosz }))
    return clientBalances([data.client], lessons, data.purchases, today)[0] ?? null
  }, [data, today])

  const name = data ? clientName(data.client) : '…'

  return (
    <Column
      density="studio"
      top={
        <>
          <nav aria-label="breadcrumb" className="flex min-w-0 items-center gap-2">
            <Link to="/studio/klienci" className="cl-link-quiet !font-semibold">
              Uczniowie
            </Link>
            <span aria-hidden className="text-class-ink-faint">
              ›
            </span>
            <span className="truncate font-bold">{name}</span>
          </nav>
          {data && (
            <Button className="ml-auto min-h-10 px-5" onClick={() => setBuying((b) => !b)} aria-expanded={buying}>
              + Zakup
            </Button>
          )}
        </>
      }
    >
      <Main density="studio">
        {isLoading ? (
          <Skeleton lines={5} />
        ) : !data || !balance ? (
          <p className="m-0 text-class-ink-soft">
            Nie ma takiego ucznia. <Link to="/studio/klienci">Wróć do listy</Link>
          </p>
        ) : (
          <>
            <Header
              client={data.client}
              onEdit={() => setEditing((e) => !e)}
              editing={editing}
              tags={data.client.tags.map((t) => ({ tag: t, color: tagColors.get(t) }))}
            />

            {editing && (
              <section className="cl-card flex flex-col gap-4 p-6">
                <ClientForm
                  initial={data.client}
                  tagColors={tagColors}
                  onSubmit={(input) => update.mutate(input)}
                  busy={update.isPending}
                  error={update.isError ? 'Nie udało się zapisać.' : ''}
                  submitLabel="Zapisz"
                />
                <button
                  type="button"
                  className="cl-link-quiet self-start text-class-gloss !text-class-wrong"
                  onClick={() => window.confirm(`Usunąć ${name}? Da się usunąć tylko ucznia bez lekcji i zakupów.`) && remove.mutate()}
                >
                  Usuń ucznia
                </button>
              </section>
            )}

            {buying && (
              <PurchaseForm
                today={today}
                onSubmit={(input) => addPurchase.mutate(input)}
                onCancel={() => setBuying(false)}
                busy={addPurchase.isPending}
                error={addPurchase.isError ? 'Nie udało się dodać.' : ''}
              />
            )}

            <section className="flex flex-col gap-3">
              <StatGrid>
                <Stat
                  label="Lekcje do wykorzystania"
                  value={balance.lessonsBought > 0 ? `${Math.max(0, balance.lessonsRemaining)} z ${balance.lessonsBought}` : '—'}
                />
                <Stat label="Lekcje odbyte" value={balance.lessonsDone} hint={balance.lessonsPlanned > 0 ? `${balance.lessonsPlanned} zaplanowane` : undefined} />
                <Stat label="Wpłacone" value={formatPrice(balance.paidGrosz)} hint={`wartość lekcji ${formatPrice(balance.earnedGrosz)}`} />
              </StatGrid>
              <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-class-gloss text-class-ink-soft">
                <BalanceStatus grosz={balance.balanceGrosz} />
                {data.client.defaultPriceGrosz > 0 && <span>· stawka {formatPrice(data.client.defaultPriceGrosz)} za lekcję</span>}
                <span className="text-class-ink-faint">· saldo = wpłaty − wartość odbytych lekcji</span>
              </p>
            </section>

            <section className="grid items-start gap-10 desk:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] desk:gap-12">
              <div className="flex flex-col gap-12">
                <PlanSection clientId={data.client.id} />
                {data.client.userId ? <Courses clientId={data.client.id} /> : null}
                <Purchases
                  purchases={data.purchases}
                  onDelete={(pid) => window.confirm('Usunąć ten zakup?') && deletePurchase.mutate(pid)}
                />
                <Lessons lessons={data.lessons} />
              </div>
              <div className="flex flex-col gap-12">
                <Block title="Notatki">
                  {data.client.notes ? (
                    <p className="m-0 whitespace-pre-wrap text-class-gloss text-class-ink-soft">{data.client.notes}</p>
                  ) : (
                    <button type="button" className="cl-link self-start text-class-gloss" onClick={() => setEditing(true)}>
                      + Dodaj notatkę
                    </button>
                  )}
                </Block>
                <Account client={data.client} />
              </div>
            </section>
          </>
        )}
      </Main>
    </Column>
  )
}

function Header({
  client,
  onEdit,
  editing,
  tags,
}: {
  client: Client
  onEdit: () => void
  editing: boolean
  tags: { tag: string; color?: string }[]
}) {
  const since = DateTime.fromJSDate(new Date(client.createdAt)).setLocale('pl').toFormat('MMMM yyyy')
  return (
    <section className="flex flex-wrap items-start gap-5">
      <Avatar name={clientName(client)} size={56} />
      <div className="min-w-0 flex-1">
        <h1 className="cl-title m-0">{clientName(client)}</h1>
        <p className="m-0 mt-1 text-class-ink-soft">
          {[client.email, client.phone].filter(Boolean).join(' · ') || 'Brak danych kontaktowych'} · uczy się od {since}
        </p>
        {(tags.length > 0 || !client.active) && (
          <p className="m-0 mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {tags.map((t) => (
              <span key={t.tag} className="inline-flex items-center gap-1.5 text-class-label font-semibold text-class-ink-soft">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: t.color ?? 'var(--color-class-ink-faint)' }} />
                {t.tag}
              </span>
            ))}
            {!client.active && <Status tone="bad">nieaktywny</Status>}
          </p>
        )}
      </div>
      <button type="button" onClick={onEdit} className="cl-link text-class-gloss" aria-expanded={editing}>
        {editing ? 'Zamknij edycję' : 'Edytuj dane'}
      </button>
    </section>
  )
}

function BalanceStatus({ grosz }: { grosz: number }) {
  if (grosz < 0) return <Status tone="wait" className="!text-class-gloss">do zapłaty {formatPrice(-grosz)}</Status>
  if (grosz > 0) return <Status tone="ok" className="!text-class-gloss">nadpłata {formatPrice(grosz)}</Status>
  return <Status tone="ok" className="!text-class-gloss">rozliczone</Status>
}

function Block({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="flex flex-col">
      <div className="flex items-baseline border-b border-class-line pb-3">
        <h2 className="cl-subheading m-0">{title}</h2>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="flex flex-col pt-3">{children}</div>
    </section>
  )
}

/* ── courses: what the student can open, and „Aktywuj kurs” ────────────── */

function Courses({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const courses = useQuery({ queryKey: ['studio', 'courses'], queryFn: api.studio.courses })
  const enrollments = useQuery({ queryKey: ['studio', 'client', clientId, 'enrollments'], queryFn: () => api.studio.enrollments(clientId) })
  const [pick, setPick] = useState('')
  const [done, setDone] = useState('')
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['studio', 'client', clientId, 'enrollments'] })
  const grant = useMutation({
    mutationFn: (courseId: string) => api.studio.enroll(clientId, courseId),
    onSuccess: (_r, courseId) => {
      setDone(courses.data?.find((c) => c.id === courseId)?.title ?? '')
      setPick('')
      refresh()
    },
  })
  const revoke = useMutation({ mutationFn: api.studio.revokeEnrollment, onSuccess: refresh })

  const active = (enrollments.data ?? []).filter((e) => !e.revokedAt)
  const available = (courses.data ?? []).filter((c) => !active.some((e) => e.courseId === c.id))

  return (
    <section className="flex flex-col">
      <h2 className="cl-subheading m-0 border-b border-class-line pb-3">Kursy</h2>
      {active.length === 0 ? (
        <p className="m-0 border-b border-class-line py-4 text-class-gloss text-class-ink-soft">Żaden kurs nie jest jeszcze aktywny.</p>
      ) : (
        <div className="flex flex-col [&>*]:border-b [&>*]:border-class-line">
          {active.map((e) => {
            const pct = e.lessons ? Math.round((e.completed / e.lessons) * 100) : 0
            const until = e.expiresAt ? `dostęp do ${DateTime.fromISO(e.expiresAt).toFormat('dd.MM.yyyy')}` : 'dostęp bez końca'
            return (
              <div key={e.id} className="flex min-h-[60px] items-center gap-4 py-2">
                <span className="min-w-0 flex-1 leading-snug">
                  <span className="block font-semibold">{e.courseTitle}</span>
                  <span className="text-class-gloss text-class-ink-soft">
                    {e.completed === 0 ? 'jeszcze nie zaczęty' : e.nextLesson ? `Lekcja ${e.nextLesson}` : 'ukończony'} · {until}
                    {e.source === 'purchase' && ' · kupiony'}
                  </span>
                </span>
                <span className="text-class-label font-semibold text-class-ink-soft">{e.completed === 0 ? 'nowy' : `${pct}%`}</span>
                <button
                  type="button"
                  className="cl-link-quiet text-class-label"
                  disabled={revoke.isPending}
                  onClick={() => window.confirm(`Cofnąć dostęp do „${e.courseTitle}”? Postęp ucznia zostanie zachowany.`) && revoke.mutate(e.id)}
                >
                  cofnij
                </button>
              </div>
            )
          })}
        </div>
      )}
      {available.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3">
          <label htmlFor="grant" className="sr-only">
            Aktywuj kurs ręcznie
          </label>
          <select id="grant" className="cl-input min-w-0 flex-1" value={pick} onChange={(ev) => setPick(ev.target.value)}>
            <option value="">Aktywuj kurs ręcznie…</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
                {c.published ? '' : ' (szkic)'}
              </option>
            ))}
          </select>
          <Button variant="secondary" disabled={!pick || grant.isPending} onClick={() => grant.mutate(pick)}>
            Aktywuj
          </Button>
        </div>
      )}
      {done && !grant.isPending && (
        <p role="status" className="m-0 mt-3 text-class-gloss font-semibold text-class-correct">
          ✓ „{done}” jest aktywny — uczeń zobaczy go w swoim panelu.
        </p>
      )}
      {grant.isError && <ErrorLine>Nie udało się aktywować.</ErrorLine>}
    </section>
  )
}

/* ── purchases ─────────────────────────────────────────────────────────── */

function Purchases({
  purchases,
  onDelete,
}: {
  purchases: { id: string; purchasedAt: string; kind: PurchaseKind; lessons: number; totalGrosz: number; note: string }[]
  onDelete: (id: string) => void
}) {
  return (
    <section className="flex flex-col">
      <h2 className="cl-subheading m-0 border-b border-class-line pb-3">Zakupy</h2>
      {purchases.length === 0 ? (
        <p className="m-0 border-b border-class-line py-4 text-class-gloss text-class-ink-soft">Jeszcze nic nie kupił — „+ Zakup” u góry.</p>
      ) : (
        <div className="flex flex-col [&>*]:border-b [&>*]:border-class-line">
          {[...purchases]
            .sort((a, b) => b.purchasedAt.localeCompare(a.purchasedAt))
            .map((p) => (
              <div key={p.id} className="group flex min-h-14 items-center gap-6 py-2">
                <span className="w-[92px] text-class-gloss text-class-ink-soft">{DateTime.fromISO(p.purchasedAt).toFormat('dd.MM.yyyy')}</span>
                <span className="min-w-0 flex-1 leading-snug">
                  <span className="block">
                    {p.lessons === 1 ? '1 lekcja' : `${PURCHASE_KIND_LABELS[p.kind]} · ${p.lessons} lekcji`}
                  </span>
                  {(p.note || p.lessons > 1) && (
                    <span className="text-class-label text-class-ink-faint">
                      {[p.lessons > 1 ? `${formatPrice(Math.round(p.totalGrosz / p.lessons))} za lekcję` : '', p.note].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
                <span className="font-bold">{formatPrice(p.totalGrosz)}</span>
                <button type="button" className="cl-link-quiet text-class-label opacity-60 group-hover:opacity-100" onClick={() => onDelete(p.id)}>
                  usuń
                </button>
              </div>
            ))}
        </div>
      )}
    </section>
  )
}

function PurchaseForm({
  today,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  today: string
  onSubmit: (input: PurchaseInput) => void
  onCancel: () => void
  busy: boolean
  error: string
}) {
  const [kind, setKind] = useState<PurchaseKind>('package')
  const [lessons, setLessons] = useState('10')
  const [total, setTotal] = useState('')
  const [date, setDate] = useState(today)
  const [note, setNote] = useState('')

  const lessonsNum = Number(lessons)
  const totalNum = Number(total.replace(',', '.'))
  const valid = Number.isInteger(lessonsNum) && lessonsNum > 0 && total.trim() !== '' && Number.isFinite(totalNum) && totalNum >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(date)
  const perLesson = valid ? zlotyToGrosz(totalNum) / lessonsNum : 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) onSubmit({ kind, lessons: lessonsNum, totalGrosz: zlotyToGrosz(totalNum), purchasedAt: date, note })
      }}
      className="cl-card grid gap-4 p-6 desk:grid-cols-5"
    >
      <p className="cl-subheading m-0 desk:col-span-5">Nowy zakup</p>
      <div>
        <FieldLabel htmlFor="p-kind">Rodzaj</FieldLabel>
        <select
          id="p-kind"
          className="cl-input"
          value={kind}
          onChange={(e) => {
            const k = e.target.value as PurchaseKind
            setKind(k)
            if (k === 'single') setLessons('1')
          }}
        >
          {PURCHASE_KINDS.map((k) => (
            <option key={k} value={k}>
              {PURCHASE_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <FieldLabel htmlFor="p-lessons">Lekcji</FieldLabel>
        <input id="p-lessons" inputMode="numeric" className="cl-input" value={lessons} onChange={(e) => setLessons(e.target.value)} />
      </div>
      <div>
        <FieldLabel htmlFor="p-total">Razem (zł)</FieldLabel>
        <input id="p-total" inputMode="decimal" className="cl-input" value={total} onChange={(e) => setTotal(e.target.value)} />
        {perLesson > 0 && <p className="m-0 mt-1 text-class-label text-class-ink-faint">{formatPrice(Math.round(perLesson))} za lekcję</p>}
      </div>
      <div>
        <FieldLabel htmlFor="p-date">Data</FieldLabel>
        <input id="p-date" type="date" className="cl-input" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div>
        <FieldLabel htmlFor="p-note">Notatka</FieldLabel>
        <input id="p-note" className="cl-input" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {error && (
        <div className="desk:col-span-5">
          <ErrorLine>{error}</ErrorLine>
        </div>
      )}
      <div className="flex items-center gap-4 desk:col-span-5">
        <Button type="submit" variant="secondary" disabled={!valid || busy}>
          {busy ? 'Zapisuję…' : 'Dodaj zakup'}
        </Button>
        <button type="button" className="cl-link-quiet" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </form>
  )
}

/* ── every lesson ──────────────────────────────────────────────────────── */

function Lessons({
  lessons,
}: {
  lessons: { id: string; day: string; startsAt: string | Date; endsAt: string | Date; status: BookingStatus; priceGrosz: number; notes: string }[]
}) {
  const [all, setAll] = useState(false)
  const shown = all ? lessons : lessons.slice(-8)
  return (
    <section className="flex flex-col">
      <div className="flex items-baseline border-b border-class-line pb-3">
        <h2 className="cl-subheading m-0">Lekcje</h2>
        <Link to="/studio/kalendarz" className="ml-auto text-class-gloss font-semibold">
          Kalendarz ›
        </Link>
      </div>
      {lessons.length === 0 ? (
        <p className="m-0 border-b border-class-line py-4 text-class-gloss text-class-ink-soft">Żadnej lekcji jeszcze nie ma — wpisz pierwszą w kalendarzu.</p>
      ) : (
        <>
          {!all && lessons.length > shown.length && (
            <button type="button" className="cl-link self-start py-3 text-class-gloss" onClick={() => setAll(true)}>
              Pokaż wszystkie ({lessons.length})
            </button>
          )}
          <div className="flex flex-col [&>*]:border-b [&>*]:border-class-line">
            {shown.map((l) => (
              <div key={l.id} className={['flex min-h-12 items-center gap-4 py-1.5 text-class-gloss', l.status === 'cancelled' ? 'opacity-60' : ''].join(' ')}>
                <span className="w-[150px]">
                  {DateTime.fromISO(l.day).toFormat('dd.MM.yyyy')} · {timeIn(l.startsAt, OWNER_TIMEZONE)}
                </span>
                <Status tone={statusTone[l.status]}>{BOOKING_STATUS_LABELS[l.status]}</Status>
                <span className="min-w-0 flex-1 truncate text-class-ink-faint">{l.notes}</span>
                <span className="font-semibold">{formatPrice(l.priceGrosz)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

/* ── the student's platform login ──────────────────────────────────────
 * Diana creates the account, then sends the link herself — by Telegram,
 * WhatsApp, whatever she already uses with that person. There is no mailer,
 * and even when there is one, handing her the link keeps her in control of
 * how it arrives.
 */
function Account({ client }: { client: Client }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState(client.email)
  const [link, setLink] = useState('')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['studio', 'client', client.id] })

  const create = useMutation({
    mutationFn: () => api.studio.createStudentAccount(client.id, email.trim(), clientName(client)),
    onSuccess: (res) => {
      setError('')
      setLink(res.inviteUrl)
      refresh()
    },
    onError: (e: unknown) => {
      const code = (e as { message?: string }).message
      setError(code === 'email_taken' ? 'Ten adres ma już konto. Użyj innego.' : code === 'already_linked' ? 'Ten uczeń ma już konto.' : 'Nie udało się założyć konta.')
    },
  })
  const renew = useMutation({
    mutationFn: () => api.studio.newInvite(client.id),
    onSuccess: (res) => {
      setError('')
      setLink(res.inviteUrl)
      setCopied(false)
    },
    onError: () => setError('Nie udało się wygenerować linku.'),
  })
  const busy = create.isPending || renew.isPending

  return (
    <Block title="Konto">
      {!client.userId ? (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-class-gloss text-class-ink-soft">
            Załóż konto, żeby uczeń widział swoje lekcje, płatności i kursy. Hasło ustawi sam — Ty wysyłasz mu tylko link.
          </p>
          <div>
            <FieldLabel htmlFor="acc-email">E-mail do logowania</FieldLabel>
            <input id="acc-email" type="email" className="cl-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="uczen@example.com" />
          </div>
          <Button variant="secondary" className="self-start" disabled={busy || !email.trim()} onClick={() => create.mutate()}>
            {create.isPending ? 'Zakładam…' : 'Utwórz konto i link'}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Status tone="ok" className="!text-class-gloss">
            Aktywne — loguje się przez /login
          </Status>
          <button type="button" className="cl-link self-start text-class-gloss" disabled={busy} onClick={() => renew.mutate()}>
            {renew.isPending ? 'Generuję…' : 'Nowy link do ustawienia hasła'}
          </button>
          <p className="m-0 text-class-label text-class-ink-faint">Nowy link unieważnia poprzedni.</p>
        </div>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
      {link && (
        <div className="mt-4 flex flex-col gap-2 rounded-class-md bg-class-white p-4 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
          <p className="m-0 font-semibold">Wyślij ten link uczniowi</p>
          <p className="m-0 text-class-label text-class-ink-faint">Widzisz go tylko teraz. Ważny 14 dni, działa raz.</p>
          <div className="flex gap-2">
            <input readOnly value={link} onFocus={(e) => e.target.select()} className="cl-input min-w-0 flex-1 text-class-label" />
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link)
                  setCopied(true)
                } catch {
                  setCopied(false)
                }
              }}
            >
              {copied ? '✓ Skopiowano' : 'Kopiuj'}
            </Button>
          </div>
        </div>
      )}
    </Block>
  )
}
